import { computed, reactive, shallowRef } from 'vue'
import type { EstimatedTimeName } from '@gat-ship/types'
import type {
  AddEstimatedTimeNamePayload,
  EstimatedTimeNamesService,
  UpdateEstimatedTimeNamePayload
} from '@gat-ship/types/injection-keys'
import {
  findEstimatedTimeNameByReferenceTime,
  selectActiveEstimatedTimeNames,
  sortEstimatedTimeNamesByReferenceTime
} from '../model/estimated-time-names-selectors'
import {
  addEstimatedTimeNameRequest,
  getEstimatedTimeNamesRequest,
  removeEstimatedTimeNameRequest,
  updateEstimatedTimeNameRequest
} from './estimated-time-names-api-calls'

// Module-singleton state, ref-data style (see useSelectAgencyPortsDataSource
// in `@gat-ship/ref-data`): every datasource instance shares one registry,
// so a mutation made in one tree (the administration modal) is
// immediately visible to every other consumer (the create-portcall
// pickers). The registry is tenant-level reference data — there is no
// per-tree variant of it.
const estimatedTimeNames = shallowRef<EstimatedTimeName[]>([])
const isLoading = reactive({ estimatedTimeNames: false })

let inFlightFetch: Promise<EstimatedTimeName[]> | undefined
let isFetched = false

function setEstimatedTimeNames(items: EstimatedTimeName[]) {
  estimatedTimeNames.value = sortEstimatedTimeNamesByReferenceTime(items)
}

async function fetch(params?: {
  forceFetch?: boolean
}): Promise<EstimatedTimeName[]> {
  if (isFetched && !params?.forceFetch) {
    return estimatedTimeNames.value
  }
  if (inFlightFetch && !params?.forceFetch) {
    return inFlightFetch
  }
  isLoading.estimatedTimeNames = true
  inFlightFetch = (async () => {
    try {
      const result = await getEstimatedTimeNamesRequest()
      setEstimatedTimeNames(result)
      isFetched = true
      return estimatedTimeNames.value
    } finally {
      inFlightFetch = undefined
      isLoading.estimatedTimeNames = false
    }
  })()
  return inFlightFetch
}

async function add(
  payload: AddEstimatedTimeNamePayload
): Promise<EstimatedTimeName> {
  const created = await addEstimatedTimeNameRequest(payload)
  setEstimatedTimeNames([...estimatedTimeNames.value, created])
  return created
}

async function update(
  id: string,
  payload: UpdateEstimatedTimeNamePayload
): Promise<EstimatedTimeName> {
  const updated = await updateEstimatedTimeNameRequest(id, payload)
  const next = estimatedTimeNames.value.map((e) => (e.id === id ? updated : e))
  setEstimatedTimeNames(next)
  return updated
}

async function remove(id: string): Promise<void> {
  await removeEstimatedTimeNameRequest(id)
  setEstimatedTimeNames(estimatedTimeNames.value.filter((e) => e.id !== id))
}

const activeEstimatedTimeNames = computed(() =>
  selectActiveEstimatedTimeNames(estimatedTimeNames.value)
)

function findByReferenceTime(referenceTime: string) {
  return findEstimatedTimeNameByReferenceTime(
    estimatedTimeNames.value,
    referenceTime
  )
}

/**
 * Reactive datasource for the estimated-time-names registry.
 *
 * Mirrors the `EstimatedTimeNamesService` contract from
 * `@gat-ship/types/injection-keys`, so the value returned here can be
 * `provide()`d under `estimatedTimeNamesKey` and consumed anywhere in
 * the tree without the consumer ever importing this package.
 *
 * The underlying state is a module singleton — multiple providers hand
 * out the same registry, and mutations propagate to all of them.
 */
export function useEstimatedTimeNamesDataSource(): EstimatedTimeNamesService {
  return {
    estimatedTimeNames,
    activeEstimatedTimeNames,
    isLoading,
    fetch,
    add,
    update,
    remove,
    findByReferenceTime
  }
}
